import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal, X } from 'lucide-react';
import api from '@/lib/api';

const DATE_RANGES = [
  { label: '30D', value: '30d' },
  { label: '3M',  value: '3m'  },
  { label: '6M',  value: '6m'  },
  { label: '12M', value: '12m' },
];

interface FilterBarProps {
  labId: string;
  onLabChange: (id: string) => void;
  dateRange?: string;
  onDateRangeChange?: (range: string) => void;
  showDateFilter?: boolean;
  defaultDateRange?: string;
}

export default function FilterBar({
  labId,
  onLabChange,
  dateRange,
  onDateRangeChange,
  showDateFilter = true,
  defaultDateRange = '6m',
}: FilterBarProps) {
  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
    staleTime: 300000,
  });

  const selectedLab = (labs || []).find((l: { id: string; name: string }) => l.id === labId);
  const hasFilter = !!labId || (showDateFilter && !!dateRange && dateRange !== defaultDateRange);

  const clearAll = () => {
    onLabChange('');
    onDateRangeChange?.(defaultDateRange);
  };

  return (
    <div className="card flex items-center gap-3 flex-wrap px-3.5 py-2">
      {/* Label */}
      <div className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
        <SlidersHorizontal size={13} />
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontFamily: '"DM Mono", monospace',
        }}>
          Filters
        </span>
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />

      {/* Lab selector */}
      <div className="flex items-center gap-2">
        <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)' }}>Lab</span>
        <select
          value={labId}
          onChange={(e) => onLabChange(e.target.value)}
          style={{
            fontSize: 12,
            fontWeight: labId ? 600 : 400,
            color: labId ? 'var(--text-main)' : 'var(--text-dim)',
            background: labId ? 'var(--gold-8)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${labId ? 'rgba(176,125,26,0.3)' : 'var(--border)'}`,
            borderRadius: 4,
            padding: '4px 26px 4px 9px',
            cursor: 'pointer',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%235a5a6e' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            minWidth: 148,
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <option value="">All Labs</option>
          {(labs || []).map((l: { id: string; name: string }) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      {/* Date range */}
      {showDateFilter && onDateRangeChange && (
        <>
          <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)' }}>Range</span>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
              {DATE_RANGES.map((r) => {
                const active = dateRange === r.value;
                return (
                  <button
                    key={r.value}
                    onClick={() => onDateRangeChange(r.value)}
                    style={{
                      padding: '4px 10px',
                      fontSize: 11.5,
                      fontWeight: active ? 700 : 500,
                      background: active ? 'var(--gold)' : 'transparent',
                      color: active ? '#fff' : 'var(--text-muted)',
                      border: 'none',
                      borderRight: r.value !== '12m' ? '1px solid var(--border)' : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.12s, color 0.12s',
                    }}
                    onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'var(--gold-5)'; e.currentTarget.style.color = 'var(--gold)'; } }}
                    onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; } }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Active filter chips + clear */}
      {hasFilter && (
        <>
          <div style={{ width: 1, height: 18, background: 'var(--border)', flexShrink: 0 }} />
          <div className="flex items-center gap-1.5">
            {labId && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 100,
                background: 'var(--gold-8)', color: 'var(--gold)', border: '1px solid rgba(176,125,26,0.25)',
              }}>
                {selectedLab?.name || labId}
              </span>
            )}
            {showDateFilter && dateRange && dateRange !== defaultDateRange && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 100,
                background: 'rgba(37,99,235,0.08)', color: '#2563EB', border: '1px solid rgba(37,99,235,0.2)',
              }}>
                {DATE_RANGES.find((r) => r.value === dateRange)?.label}
              </span>
            )}
            <button
              onClick={clearAll}
              title="Clear filters"
              className="flex items-center gap-1"
              style={{
                padding: '3px 7px', background: 'none',
                border: '1px solid var(--border)', borderRadius: 100,
                fontSize: 11, color: 'var(--text-dim)', cursor: 'pointer', transition: 'all 0.12s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <X size={10} /> Clear
            </button>
          </div>
        </>
      )}
    </div>
  );
}
